import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  brokerOutreachApi,
  type BrokerOutreachFieldKitDto,
  type BrokerOutreachStateDto,
} from "@/api/brokerOutreach";
import { listingsApi } from "@/api/buyers";
import {
  Button,
  buttonVariants,
  Input,
  ResponsiveGrid,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { safeHref } from "@/design-system/lib/safeHref";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { HeaderAction } from "../introCards/HeaderAction";
import { IntroCardPill } from "../introCards/IntroCardPill";
import { draftNewerThanSend, isDone, latestVersion, outreachCardStatus } from "../introCards/introCardStatus";
import { EditAndSendAgain, SentFold } from "../introCards/SentFold";
import { writeClipboard } from "../outreach/outreachCopy";
import { brokerOutreachCopy } from "./brokerOutreachCopy";
import { CardSentLine } from "./CardStatusLine";
import { useInvalidateWithOutreachState } from "./useBrokerOutreach";

/**
 * The Web form card (D28) — the listing broker's own contact form, filled in by
 * the consultant.
 *
 * <p><b>The limit is the FORM's, so it is editable per send.</b> 500 by default,
 * never below 150; the counter runs live against it, COPY is blocked while the
 * text is over it, and SHORTEN TO FIT asks the server for a draft under the
 * current limit (the server falls back to its template, built to fit at the
 * 150 minimum, when the assistant cannot). The consultant's own edits are never
 * gated beyond that length — it is the form that would refuse them.</p>
 *
 * <p>🔴 <b>No role statement (R8).</b> A contact form is the broker's surface,
 * not Phoenix's email; the agency guard on the draft is the only control
 * there.</p>
 *
 * <p>I SUBMITTED IT records exactly what was copied and the form's URL — one
 * send version and one communications entry (buyer, submission, listing).
 * Copying writes nothing.</p>
 *
 * <p><b>A collapsible card (D2).</b> The typed text and the copied flag live
 * here, above the hidden body, so a collapse loses neither. Once logged, the
 * latest message that went through the form is folded at the top, the form
 * link stays, and the field kit, limit and composer wait behind "Edit and send
 * again" — unless the saved draft is newer than that send (J8).</p>
 */
export function BrokerWebFormCard({
  submissionId,
  state,
  canWrite,
  canEditListing,
  open,
  onOpenChange,
  onActed,
}: {
  submissionId: string;
  state: BrokerOutreachStateDto;
  canWrite: boolean;
  /** `Listings.Manage` — saving a contact form URL writes the LISTING. */
  canEditListing: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Called after an I SUBMITTED IT this tab performed succeeds (J6). */
  onActed?: () => void;
}) {
  const toast = useToast();
  const card = state.webForm;
  const draft = card.draft;
  const formUrl = state.readiness.contactFormUrl;
  const [limitText, setLimitText] = useState(String(draft?.characterLimit ?? state.webFormDefaultLimit));
  const [copied, setCopied] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const history = useSendHistory(submissionId);

  const limit = Number.parseInt(limitText, 10);
  const limitValid = Number.isFinite(limit) && limit >= state.webFormMinimumLimit;

  // The typed text, reseeded whenever a (re)draft or SHORTEN TO FIT lands.
  const saved = draft?.body ?? "";
  const [seededFrom, setSeededFrom] = useState(draft?.generatedAtUtc ?? null);
  const [text, setText] = useState(saved);
  if ((draft?.generatedAtUtc ?? null) !== seededFrom) {
    setSeededFrom(draft?.generatedAtUtc ?? null);
    setText(saved);
  }
  const length = text.trim().length;
  const composerLimit = limitValid ? limit : null;
  const over = composerLimit === null ? 0 : Math.max(0, length - composerLimit);
  const copyBlocked = length === 0 || composerLimit === null || over > 0;

  const generate = useMutation({
    mutationFn: () => brokerOutreachApi.draft(submissionId, "web-form", limit),
    onSuccess: (s) => {
      invalidateWithState(s);
      setCopied(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const log = useMutation({
    mutationFn: (body: string) =>
      brokerOutreachApi.log(submissionId, "web-form", { body, formUrl, characterLimit: limit }),
    onSuccess: (s) => {
      invalidateWithState(s, { logged: true });
      setCopied(null);
      setRevealed(false);
      toast.success("Logged. It is on the buyer's, the submission's and the listing's Communications.");
      onActed?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copy = async () => {
    const value = text.trim();
    if (await writeClipboard(value)) {
      setCopied(value);
      toast.success("Message copied.");
    } else {
      toast.error("Couldn't copy the message. Select it and copy by hand.");
    }
  };

  const status = outreachCardStatus(card, copied != null);
  const done = isDone(status);
  const latest = latestVersion(history.data?.brokerWebForms);
  const unsentDraft = draft != null && draftNewerThanSend([draft.generatedAtUtc, draft.editedAtUtc], card.sentAtUtc);
  // J8 — writers only: a reader keeps today's read-only view, with no control to reach.
  // The editor area hides by CLASS as well as the attribute: Tailwind's preflight
  // `[hidden]` rule loses to `.grid` (same specificity, emitted earlier).
  const editorHidden = canWrite && done && !revealed && !unsentDraft;

  const action = !canWrite ? null : status.state === "notDrafted" ? (
    <HeaderAction
      label={generate.isPending ? "Drafting…" : "Draft"}
      onClick={() => generate.mutate()}
      disabled={!limitValid || generate.isPending}
      reason={!limitValid ? brokerOutreachCopy.limitBelowMinimum(state.webFormMinimumLimit) : null}
      testId="broker-webform-header-draft"
    />
  ) : status.state === "copied" ? (
    <HeaderAction
      label={log.isPending ? "Logging…" : "I submitted it"}
      onClick={() => copied && log.mutate(copied)}
      disabled={log.isPending}
      testId="broker-webform-header-submitted"
    />
  ) : status.state === "draftSaved" ? (
    <HeaderAction
      label="Copy"
      onClick={() => void copy()}
      disabled={copyBlocked}
      reason={
        !limitValid
          ? brokerOutreachCopy.limitBelowMinimum(state.webFormMinimumLimit)
          : over > 0
            ? brokerOutreachCopy.overLimit(over)
            : null
      }
      testId="broker-webform-header-copy"
    />
  ) : null;

  return (
    <CollapsibleCard
      open={open}
      onOpenChange={onOpenChange}
      title="Web form"
      recipient="To the listing broker"
      pill={<IntroCardPill status={status} logged />}
      action={action}
      testId="broker-webform-card"
    >
      <div className="grid gap-space-3">
        {done ? <CardSentLine card={card} testId="broker-webform-sent-line" /> : null}
        {done && latest ? <SentFold body={latest.body} testId="broker-webform-sent" /> : null}

        <FormLink
          listingId={state.listingId}
          formUrl={formUrl}
          canEditListing={canEditListing}
        />

        {editorHidden && canWrite ? (
          <EditAndSendAgain onClick={() => setRevealed(true)} testId="broker-webform-edit-again" />
        ) : null}

        <div hidden={editorHidden} className={cn("grid gap-space-3", editorHidden && "hidden")} data-testid="broker-webform-editor">
          <FieldKit kit={state.fieldKit} />

          <label className="flex flex-wrap items-center gap-space-2 text-small">
            <span className="font-medium">The form&apos;s character limit</span>
            <Input
              type="number"
              inputMode="numeric"
              min={state.webFormMinimumLimit}
              value={limitText}
              onChange={(e) => setLimitText(e.target.value)}
              invalid={!limitValid}
              className="w-28"
              aria-label="The form's character limit"
              data-testid="broker-webform-limit"
            />
          </label>
          {!limitValid ? (
            <p className="text-small text-status-warning m-0" data-testid="broker-webform-limit-invalid">
              {brokerOutreachCopy.limitBelowMinimum(state.webFormMinimumLimit)}
            </p>
          ) : null}

          {!draft ? (
            canWrite ? (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => generate.mutate()}
                  disabled={!limitValid || generate.isPending}
                  data-testid="broker-webform-draft"
                >
                  {generate.isPending ? "Drafting…" : "Draft"}
                </Button>
              </div>
            ) : (
              <p className="text-small text-gray-mid m-0">Nothing drafted yet.</p>
            )
          ) : (
            <WebFormComposer
              submissionId={submissionId}
              saved={draft.body}
              text={text}
              onTextChange={setText}
              fromTemplate={draft.fromTemplate}
              limit={composerLimit}
              canWrite={canWrite}
              copied={copied}
              onCopy={() => void copy()}
              onShorten={() => generate.mutate()}
              shortening={generate.isPending}
              onLog={(body) => log.mutate(body)}
              logging={log.isPending}
            />
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
}

function WebFormComposer({
  submissionId,
  saved,
  text,
  onTextChange,
  fromTemplate,
  limit,
  canWrite,
  copied,
  onCopy,
  onShorten,
  shortening,
  onLog,
  logging,
}: {
  submissionId: string;
  saved: string;
  /** The typed text — held by the card so a collapse keeps it (D2). */
  text: string;
  onTextChange: (next: string) => void;
  fromTemplate: boolean;
  /** Null while the limit field holds something invalid. */
  limit: number | null;
  canWrite: boolean;
  copied: string | null;
  onCopy: () => void;
  onShorten: () => void;
  shortening: boolean;
  onLog: (body: string) => void;
  logging: boolean;
}) {
  const toast = useToast();
  const invalidateWithState = useInvalidateWithOutreachState(submissionId);
  const length = text.trim().length;
  const over = limit === null ? 0 : Math.max(0, length - limit);
  const dirty = text.trim() !== saved.trim();
  const empty = length === 0;
  const copyBlocked = empty || limit === null || over > 0;

  const save = useMutation({
    mutationFn: () => brokerOutreachApi.saveDraft(submissionId, "web-form", text, limit),
    onSuccess: (s) => {
      invalidateWithState(s);
      toast.success("Draft saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-space-3">
      <Textarea
        rows={6}
        value={text}
        readOnly={!canWrite}
        aria-label="Web form message"
        data-testid="broker-webform-body"
        onChange={(e) => onTextChange(e.target.value)}
      />
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span
          className={`text-caption ${over > 0 ? "text-status-warning-ink" : "text-gray-mid"}`}
          data-testid="broker-webform-count"
        >
          {length} / {limit ?? "—"} characters
        </span>
        <span className="text-caption text-gray-mid">
          {fromTemplate
            ? "Written from the fallback template."
            : "Drafted by the assistant from the facts on file."}
        </span>
      </div>
      {over > 0 ? (
        <p className="text-small text-status-warning m-0" data-testid="broker-webform-over">
          {brokerOutreachCopy.overLimit(over)}
        </p>
      ) : null}
      <p className="text-caption text-gray-mid m-0">{brokerOutreachCopy.noRoleStatement}</p>

      {canWrite ? (
        <>
          <div className="flex flex-wrap items-center gap-space-3">
            <Button onClick={onCopy} disabled={copyBlocked} data-testid="broker-webform-copy">
              Copy
            </Button>
            <Button
              variant="secondary"
              onClick={() => copied && onLog(copied)}
              disabled={!copied || logging}
              data-testid="broker-webform-submitted"
            >
              {logging ? "Logging…" : "I submitted it"}
            </Button>
            <Button
              variant="text"
              onClick={onShorten}
              disabled={limit === null || shortening}
              data-testid="broker-webform-shorten"
            >
              {shortening ? "Redrafting…" : over > 0 ? "Shorten to fit" : "Redraft"}
            </Button>
            <Button variant="text" onClick={() => save.mutate()} disabled={!dirty || empty || save.isPending}>
              {save.isPending ? "Saving…" : "Save draft"}
            </Button>
          </div>
          {copied ? (
            <p role="status" className="text-small text-gray-mid m-0" data-testid="broker-webform-copied">
              {brokerOutreachCopy.copiedSubmitted}
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-caption text-gray-mid m-0">Only this buyer&apos;s consultant can copy and log it.</p>
      )}
    </div>
  );
}

/**
 * OPEN FORM — or, with no form on file, a field to add one (saved to the
 * listing, D20). Agent research no longer looks for a contact form, so this
 * card offers no FIND AGENT.
 *
 * <p>The stored URL goes through `safeHref`: a value that is not http(s) renders
 * as text, never as a live link.</p>
 */
function FormLink({
  listingId,
  formUrl,
  canEditListing,
}: {
  listingId: string;
  formUrl: string | null;
  canEditListing: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState("");

  const saveUrl = useMutation({
    mutationFn: (url: string) => listingsApi.update(listingId, { contactFormUrl: url }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setValue("");
      toast.success("Contact form saved to the listing.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (formUrl) {
    const href = safeHref(formUrl);
    return (
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span className="text-small text-gray-mid min-w-0 break-all" data-testid="broker-webform-url">
          {formUrl}
        </span>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonVariants({ variant: "secondary", size: "sm" })}
            data-testid="broker-webform-open"
          >
            Open form
          </a>
        ) : null}
      </div>
    );
  }

  const trimmed = value.trim();
  return (
    <div className="grid gap-space-2" data-testid="broker-webform-no-url">
      <p className="text-small text-gray-mid m-0">{brokerOutreachCopy.noContactForm}</p>
      {canEditListing ? (
        <div className="flex flex-wrap items-center gap-space-2">
          <Input
            type="url"
            placeholder="https://…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="Contact form URL"
            className="min-w-0 flex-1"
            data-testid="broker-webform-url-input"
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => saveUrl.mutate(trimmed)}
            disabled={!safeHref(trimmed) || saveUrl.isPending}
            data-testid="broker-webform-url-save"
          >
            {saveUrl.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The field kit (D28): what the broker's form asks for, each with its own copy
 *  button. The company is "Phoenix Capital Solutions" — the sender's employer. */
function FieldKit({ kit }: { kit: BrokerOutreachFieldKitDto }) {
  const rows: Array<{ label: string; value: string | null }> = [
    { label: "Name", value: kit.fullName },
    { label: "Email", value: kit.email },
    { label: "Phone", value: kit.phone },
    { label: "Company", value: kit.company },
    { label: "Subject", value: kit.subject },
  ];
  return (
    <ResponsiveGrid cols={3} minColWidth="14rem" gap="sm" data-testid="broker-webform-field-kit">
      {rows.map((r) => (
        <KitField key={r.label} label={r.label} value={r.value} />
      ))}
    </ResponsiveGrid>
  );
}

function KitField({ label, value }: { label: string; value: string | null }) {
  const toast = useToast();
  return (
    <div className="grid gap-space-1 min-w-0">
      <div className="flex items-center justify-between gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-wider">{label}</span>
        {value ? (
          <Button
            size="sm"
            variant="text"
            onClick={() =>
              void writeClipboard(value).then((ok) =>
                ok
                  ? toast.success(`${label} copied.`)
                  : toast.error(`Couldn't copy the ${label.toLowerCase()}. Select it and copy by hand.`),
              )
            }
            aria-label={`Copy ${label.toLowerCase()}`}
          >
            Copy
          </Button>
        ) : null}
      </div>
      <span className="text-small text-onyx break-words" data-testid={`broker-webform-kit-${label.toLowerCase()}`}>
        {value ?? <span className="text-gray-mid">Not on file</span>}
      </span>
    </div>
  );
}
